/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useMemo } from 'react';
import { FREE_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import { AssetsMenuItemFreeUsersPlacement } from '@jira/growth__assets-menu-item-free-users-placement/src/AssetsMenuItemFreeUsersPlacement.tsx';
import { useAssetsWorkspaceHost } from '@atlassian/assets-workspace-host/use-assets-workspace-host';
import { getProductContext } from '@atlassian/assets-workspace-host/get-product-context';
import { useAssets } from '@atlassian/global-shortcuts/assets';
import { useConfluence } from '@atlassian/global-shortcuts/confluence';
import { useLoom } from '@atlassian/global-shortcuts/loom';
import { useGoals } from '@atlassian/global-shortcuts/goals';
import { useOpsgenie } from '@atlassian/global-shortcuts/opsgenie';
import { useProjects } from '@atlassian/global-shortcuts/projects';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { getUserNavigationItemLinks } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { GlobalAppShortcuts } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-global-apps/src/index.tsx';
import {
	getLicensedProducts,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import type {
	GlobalShortcutLinkItem,
	GlobalShortcutsItem,
} from '@atlassian/global-shortcuts/types';

import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getRenderableShortcutMenuIds } from '../use-renderable-shortcut-menu-ids/useRenderableShortcutMenuIds.tsx';

// Filter product shortcuts based on licence states.
// Keys are owned by `useRenderableShortcutMenuIds` (shared with the modal).
export const useLicenseFilterMap: (
	teamsAppConfig: GlobalShortcutsItem | undefined,
) => Record<string, GlobalShortcutsItem> = (teamsAppConfig?: GlobalShortcutsItem) => {
	const licenceStates = useLicenseStates();
	const products = getLicensedProducts();
	const hasConfluence = !!products[LICENSED_PRODUCTS.CONFLUENCE];
	const hasLoom = !!products[LICENSED_PRODUCTS.LOOM];
	const appEditions = useAppEditions();
	const isJsmFree = appEditions?.serviceDesk === FREE_EDITION;
	const isSiteAdmin = useIsSiteAdmin();
	const hasProject =
		!!products[LICENSED_PRODUCTS.PROJECT] || !!products[LICENSED_PRODUCTS.TOWNSQUARE];
	const hasGoal = !!products[LICENSED_PRODUCTS.GOAL] || !!products[LICENSED_PRODUCTS.TOWNSQUARE];

	const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();

	// cross-unit Assets host resolution.
	const cloudId = useCloudId();
	const appPermissions = useApplicationPermissions();
	const workspaceHostState = useAssetsWorkspaceHost({
		cloudId,
		productContext: getProductContext({ appPermissions }),
		skip: !fg('astral_units_workspace_host_resolver'),
	});
	const isAssetsResolverSuccess =
		workspaceHostState.status === 'success' && fg('astral_units_workspace_host_resolver');

	const teamsSupported = !!teamsAppConfig;
	const renderableMenuIds = useMemo(
		() =>
			getRenderableShortcutMenuIds({
				licenceStates,
				hasConfluence,
				hasLoom,
				hasGoal,
				hasProject,
				teamsSupported,
				assetsResolverSuccess: isAssetsResolverSuccess,
			}),
		[
			licenceStates,
			hasConfluence,
			hasLoom,
			hasGoal,
			hasProject,
			teamsSupported,
			isAssetsResolverSuccess,
		],
	);
	const resolvedAssetsUrl = isAssetsResolverSuccess
		? workspaceHostState.data.toAbsoluteUrl(
				USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_ASSETS],
			)
		: USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_ASSETS];

	const confluenceItem = useConfluence(
		USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
	);
	const loomItem = useLoom(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_LOOM]);
	const assetsItem = useAssets(resolvedAssetsUrl);
	const opsgenieItem = useOpsgenie(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_OPSGENIE]);
	const goalsItem = useGoals(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_GOALS]);
	const projectsItem = useProjects(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_PROJECTS]);

	return useMemo(() => {
		let assetsItemWithComponent = null;
		if (isSiteAdmin && isJsmFree && fg('sqa-2098-assets-nav-upsell-jsm-free-nav')) {
			assetsItemWithComponent = {
				...assetsItem,
				type: 'component' as const,
				component: () => <AssetsMenuItemFreeUsersPlacement />,
			};
		} else if (fg('astral_units_workspace_host_resolver')) {
			assetsItemWithComponent = {
				...assetsItem,
				type: 'component' as const,
				component: AssetsComponent,
			};
		} else {
			assetsItemWithComponent = {
				...assetsItem,
				component: AssetsComponent,
			};
		}

		// Build the full map of values, then project down to only the keys the helper
		// says are renderable. Constructing the full map up-front (rather than inline
		// inside each branch) keeps this path simple and ensures any future addition
		// is expressed once here, not duplicated across branches.
		const allItems: Record<
			string,
			GlobalShortcutsItem | (GlobalShortcutLinkItem & { component: unknown })
		> = {
			[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluenceItem,
			[L1_MENU_ID.GLOBAL_APP_LOOM]: loomItem,
			[L1_MENU_ID.GLOBAL_APP_ASSETS]: assetsItemWithComponent,
			[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenieItem,
			[L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem,
			[L1_MENU_ID.GLOBAL_APP_PROJECTS]: projectsItem,
			...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
		};

		return renderableMenuIds.reduce<Record<string, GlobalShortcutsItem>>((acc, menuId) => {
			if (allItems[menuId]) {
				acc[menuId] = allItems[menuId];
			}
			return acc;
		}, {});
	}, [
		assetsItem,
		confluenceItem,
		goalsItem,
		isJsmFree,
		isSiteAdmin,
		loomItem,
		opsgenieItem,
		projectsItem,
		renderableMenuIds,
		teamsAppConfig,
	]);
};

const AssetsComponent = () => <GlobalAppShortcuts menuId={L1_MENU_ID.GLOBAL_APP_ASSETS} />;
